import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { useLanguage } from '../hooks/useLanguage';
import type { Product, WeightVariant } from '../components/ProductCard';

interface PageLayoutProps {
  children: ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  // Header props
  onCartClick?: () => void;
  onCatalogClick?: () => void;
  onRecipesClick?: () => void;
  onMenuClick?: () => void;
  onCategoryClick?: (category: string, subcategory?: string) => void;
  onAccountSettingsClick?: () => void;
  onSearch?: (query: string) => void;
  onProductClick?: (product: Product) => void;
  onRecipeClick?: (recipe?: any) => void;
  onAddToCart?: (product: Product, variant?: WeightVariant) => void;
  // Footer props
  onPageNavigation?: (page: string) => void;
}

export function PageLayout({
  children,
  showHeader = true,
  showFooter = true,
  seoTitle,
  seoDescription,
  onCartClick,
  onCatalogClick,
  onRecipesClick,
  onMenuClick,
  onCategoryClick,
  onAccountSettingsClick,
  onSearch,
  onProductClick,
  onRecipeClick,
  onAddToCart,
  onPageNavigation,
}: PageLayoutProps) {
  const { t } = useTranslation('meta');
  const { currentLanguage, getAllLanguageUrls } = useLanguage();

  // Get current path and full URL for SEO
  const currentPath = window.location.pathname;
  const currentUrl = `https://olispices.com${currentPath}`;

  // Translate SEO titles and descriptions
  const translatedTitle = seoTitle ? t(seoTitle) : undefined;
  const translatedDescription = seoDescription ? t(seoDescription) : undefined;

  return (
    <>
      {/* SEO Head - Always included */}
      <SEOHead
        title={translatedTitle}
        description={translatedDescription}
        url={currentUrl}
        language={currentLanguage}
        alternateUrls={getAllLanguageUrls(currentPath)}
      />

      {/* Header - Conditional */}
      {showHeader && (
        <Header
          onCartClick={onCartClick}
          onCatalogClick={onCatalogClick}
          onRecipesClick={onRecipesClick}
          onMenuClick={onMenuClick}
          onCategoryClick={onCategoryClick}
          onAccountSettingsClick={onAccountSettingsClick}
          onSearch={onSearch}
          onProductClick={onProductClick}
          onRecipeClick={onRecipeClick}
          onAddToCart={onAddToCart}
        />
      )}

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer - Conditional */}
      {showFooter && <Footer onPageNavigation={onPageNavigation} />}
    </>
  );
}
